<script setup lang="ts">
import type { AnalysisFrameRect } from "@/composable/useAnalysisFrames";

/**
 * Un cadre sur le plan : rectangle d'état + badge. Le rectangle laisse passer
 * les touchers vers le dessin ; seul le badge est interactif. Deux usages :
 * un groupe d'analyse (violet, n°, états à valider / validé / ignoré — le
 * badge rend le groupe courant) et une observation réelle (bleu, lettre — le
 * badge ouvre sa carte, ou la classe dans la partie sélectionnée : vert si
 * elle y est, en retrait sinon).
 */

defineProps<{
  frame: AnalysisFrameRect;
}>();

defineEmits<{
  (e: "select", groupId: string): void;
}>();
</script>

<template>
  <div
    class="analysis-frame"
    :class="[
      frame.tone === 'observation'
        ? 'tone-observation'
        : `status-${frame.status}`,
      {
        current: frame.isCurrent,
        manual: frame.manual,
        assigned: frame.assigned === true,
        unassigned: frame.assigned === false,
      },
    ]"
    :style="{
      left: `${frame.left}px`,
      top: `${frame.top}px`,
      width: `${frame.width}px`,
      height: `${frame.height}px`,
    }"
  >
    <!-- touchstart.stop : le conteneur du canvas fait `@touchstart.prevent`,
         qui supprimerait le click synthétique du badge sur tablette -->
    <button
      type="button"
      class="frame-badge"
      @pointerdown.stop
      @touchstart.stop
      @click.stop="$emit('select', frame.id)"
    >
      {{ frame.label ?? frame.number }}
    </button>
  </div>
</template>

<style lang="scss" scoped>
.analysis-frame {
  position: absolute;
  border-radius: 4px;
  // Le cadre n'avale aucun toucher : le dessin reste utilisable dessous.
  pointer-events: none;

  &.status-pending {
    border: 2px dashed var(--dr-report-light);
  }
  &.status-validated {
    border: 2px solid var(--bs-success);
  }
  &.status-ignored {
    border: 2px dashed var(--canvas-btn-disabled-color);
    opacity: 0.6;
  }
  &.current {
    border: 3px solid var(--dr-report);
    box-shadow:
      0 0 0 3px color-mix(in srgb, var(--dr-report) 40%, transparent),
      0 0 18px 4px color-mix(in srgb, var(--dr-report-light) 55%, transparent),
      inset 0 0 14px color-mix(in srgb, var(--dr-report) 22%, transparent);
    animation: analysis-frame-pulse 1.8s ease-in-out infinite;
  }
}

// Observation réelle : bleu plein, pour la distinguer des groupes détectés.
.analysis-frame.tone-observation {
  border: 2px solid var(--bs-primary);

  &.current {
    border: 3px solid var(--bs-primary);
    animation: none;
    box-shadow:
      0 0 0 3px color-mix(in srgb, var(--bs-primary) 40%, transparent),
      0 0 18px 4px color-mix(in srgb, var(--bs-primary) 55%, transparent),
      inset 0 0 14px color-mix(in srgb, var(--bs-primary) 22%, transparent);
  }

  .frame-badge {
    background-color: var(--bs-primary);
  }
}

// Mode association (onglet Parties) : les observations de la partie
// sélectionnée en vert plein, les autres en retrait — le badge reste
// touchable pour (dé)rattacher. Après le ton « observation » : à spécificité
// égale, c'est ce bloc qui doit l'emporter sur le bleu.
.analysis-frame.assigned {
  border: 2px solid var(--bs-success) !important;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--bs-success) 35%, transparent) !important;
  animation: none !important;

  .frame-badge {
    background-color: var(--bs-success);
  }
}
// Hors de la partie : seul le contour s'efface. Le badge reste plein — c'est
// la cible à toucher, et le bleu estompé s'éteindrait sur le fond du plan.
.analysis-frame.unassigned {
  border-color: color-mix(in srgb, var(--bs-primary) 50%, transparent);
  animation: none !important;
  box-shadow: none !important;
}

// Groupe créé à la main : sarcelle, pour le distinguer des détections violettes.
.analysis-frame.manual {
  &.status-pending {
    border-color: var(--bs-teal);
  }
  &.current {
    border-color: var(--bs-teal);
    animation: none;
    box-shadow:
      0 0 0 3px color-mix(in srgb, var(--bs-teal) 40%, transparent),
      0 0 18px 4px color-mix(in srgb, var(--bs-teal) 55%, transparent),
      inset 0 0 14px color-mix(in srgb, var(--bs-teal) 22%, transparent);
  }

  :deep(.frame-badge),
  .frame-badge {
    background-color: var(--bs-teal);
  }
}

@keyframes analysis-frame-pulse {
  0%,
  100% {
    box-shadow:
      0 0 0 3px color-mix(in srgb, var(--dr-report) 40%, transparent),
      0 0 18px 4px color-mix(in srgb, var(--dr-report-light) 55%, transparent),
      inset 0 0 14px color-mix(in srgb, var(--dr-report) 22%, transparent);
  }
  50% {
    box-shadow:
      0 0 0 3px color-mix(in srgb, var(--dr-report) 28%, transparent),
      0 0 10px 2px color-mix(in srgb, var(--dr-report-light) 35%, transparent),
      inset 0 0 8px color-mix(in srgb, var(--dr-report) 14%, transparent);
  }
}

.frame-badge {
  position: absolute;
  top: -11px;
  left: -11px;
  min-width: 22px;
  height: 22px;
  padding: 0 4px;
  border: none;
  border-radius: 11px;
  background-color: var(--dr-report);
  color: white;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 22px;
  cursor: pointer;
  // Seul élément interactif du cadre.
  pointer-events: auto;

  .status-validated & {
    background-color: var(--bs-success);
  }
  .status-ignored & {
    background-color: var(--canvas-btn-disabled-color);
  }
}
</style>
